import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import ErrorBoundary from '../../../Core/ErrorHandling/ErrorBoundary';
import { EventActionsEnum, EventCategoriesEnum } from '../../../Services/Logging/DataLayerDefinitions';
import loggingService from '../../../Services/Logging/LoggingService';
import trackingService, { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import useSearchParameters from '../Hooks/useSearchParametersHook';
import InlineTab from './InlineTab';
import styles from './InlineTabs.module.scss';
import { IInlineTabOptions } from './InlineTabsDefinitions';

interface IInlineTabsProps {
    inlineTabsKey: string;
    children?: ReactNode;
    activeTab?: string;
    defaultTab?: string;
    tabsRightSide?: JSX.Element;
    testId?: string;
    track: TrackingEvent;
}

const InlineTabs: React.FC<IInlineTabsProps> = (props: IInlineTabsProps) => {
    const [searchParameters, setSearchParameters] = useSearchParameters();
    const [activeTab, setActiveTab] = useState(searchParameters[props.inlineTabsKey] || '');

    const tabs = useMemo(
        () =>
            React.Children.toArray(props.children)
                .filter((child) => React.isValidElement(child) && child.type === InlineTab)
                .map((child) => {
                    const element = child as React.ReactElement<IInlineTabOptions>;
                    return {
                        inlineTabKey: element.props.inlineTabKey,
                        title: element.props.title,
                        show: element.props.show !== false,
                        testId: element.props.testId,
                        component: element.props.children,
                        tabRightSide: element.props.tabRightSide,
                    };
                }),
        [props.children],
    );

    useEffect(() => {
        if (!(props.inlineTabsKey in searchParameters) || searchParameters[props.inlineTabsKey] === '') {
            const defaultTabExists = props.defaultTab && tabs.some((tab) => tab.show && tab.inlineTabKey === props.defaultTab);
            const tabKey = defaultTabExists ? props.defaultTab! : tabs.find((tab) => tab.show)?.inlineTabKey || '';
            setActiveTab(tabKey);
        }
    }, [tabs, searchParameters]);

    useEffect(() => {
        if (!props.activeTab || activeTab === props.activeTab) {
            return;
        }

        setActiveTab(props.activeTab);
    }, [props.activeTab]);

    useEffect(() => {
        if (searchParameters[props.inlineTabsKey] && searchParameters[props.inlineTabsKey] === activeTab) {
            return;
        }

        if (activeTab) {
            setSearchParameters({ [props.inlineTabsKey]: activeTab });
            loggingService.trackEventWithAnalytics(EventActionsEnum.Navigation, EventCategoriesEnum.TabChange, activeTab);
            trackingService.track({ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}Changed`, eventValue: activeTab });
        }
    }, [activeTab]);

    return (
        <>
            <div className={styles.inline_tab_main_container} data-testid={props.testId}>
                {tabs
                    .filter((tab) => tab.show)
                    .map((tab) => (
                        <div
                            key={`inlinetab_${tab.inlineTabKey}`}
                            className={
                                tab.inlineTabKey === searchParameters[props.inlineTabsKey] ? styles.inline_tab_container_selected : styles.inline_tab_container
                            }
                            data-testid={tab.testId}
                        >
                            <button
                                type="button"
                                key={`inlinetab_${tab.inlineTabKey}`}
                                className={
                                    tab.inlineTabKey === searchParameters[props.inlineTabsKey] ? styles.inline_tab_button_selected : styles.inline_tab_button
                                }
                                onClick={() => setActiveTab(tab.inlineTabKey)}
                            >
                                {tab.title}
                            </button>
                            {tab.tabRightSide && <div className={styles.inline_tab_tab_right_side}>{tab.tabRightSide}</div>}
                        </div>
                    ))}
                <div className={styles.inline_tab_empty_underline_space}>
                    {props.tabsRightSide && <div className={styles.inline_tab_tabs_right_side}>{props.tabsRightSide}</div>}
                </div>
            </div>
            <ErrorBoundary>
                <div>{tabs.find((tab) => tab.show && tab.inlineTabKey === searchParameters[props.inlineTabsKey])?.component}</div>
            </ErrorBoundary>
        </>
    );
};

export default InlineTabs;
